import { SingleLogEntryPage } from '@monorepo/logs/src/components/SingleLogEntryPage';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { RoutesWith404 } from '@monorepo/shared/componentsV2/RoutesWith404';
import { useIsLogAiEnabled } from '@monorepo/shared/hooks/logs/useIsLogAiEnabled';
import { useFeatureFlags } from 'mapistry-shared/api';
import React from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router-dom';
import { AiSubmissionPage } from './components/AiSubmissionPage';
import { LogDocumentsTab } from './components/LogDocumentsTab';
import { LogEntriesTab } from './components/LogEntriesTab';
import { LogFrequencyRequirementsTab } from './components/LogFrequencyRequirements';
import { LogsAndViewsPage } from './components/LogsAndViewsPage';
import { LogsPageContent } from './components/LogsPage';
import { EmptyLogsSalesPage } from './components/LogsPage/EmptyLogsSalesPage';
import { SingleLogPage } from './components/SingleLogPage';
import { EditViewPage } from './components/Views/EditViewPage';
import { SingleViewPage } from './components/Views/SingleViewPage';
import { ViewDetailTab } from './components/Views/ViewDetailTab';
import { ViewsPageContent } from './components/Views/ViewsPage/ViewsPageContent';

/**
 * Views used to belong to a single Log, and were accessed via that single Log's page.
 * With Workflows, all Reports belong to a Project, and are thus nested under the Project url wise,
 * but not the specific log on that project. This component forwards all view pages from the log specific urls
 * to the corresponding project view ones (essentially just taking the `/:logId/` bit out of the path).
 */
function RedirectToPageOnViewsTab() {
  const { logId } = useParams();
  const { pathname } = useLocation();

  // going from log views to project views page, so from
  // `/:siteId/logs/:logId/views/:viewId` to
  // `/:siteId/logs/views/:viewId`
  const pathUnderProjectViews = pathname.replace(`/${logId}`, '');

  return <Navigate replace to={pathUnderProjectViews} />;
}

export function LogsRouter() {
  const { isData2Enabled } = useFeatureFlags();
  const { organizationId } = useParams();
  const isLogAiEnabled = useIsLogAiEnabled(organizationId);

  if (!isData2Enabled) {
    return <EmptyLogsSalesPage />;
  }

  return (
    <RoutesWith404>
      <Route path="" element={<LogsAndViewsPage />}>
        <Route index element={<LogsPageContent />} />
        <Route path="views">
          <Route index element={<ViewsPageContent />} />
          <Route path="new" element={<EditViewPage />} />
          <Route path=":viewId" element={<SingleViewPage />}>
            <Route index element={<Navigate replace to="pages" />} />
            <Route path="pages">
              <Route index element={<Navigate replace to="0" />} />
              <Route path=":pageNumber">
                <Route index element={<ViewDetailTab />} />
              </Route>
            </Route>
            <Route path="edit" element={<EditViewPage />} />
          </Route>
        </Route>
      </Route>
      <Route
        path=":logId"
        element={
          <ErrorBoundary>
            <SingleLogPage />
          </ErrorBoundary>
        }
      >
        <Route index element={<Navigate replace to="records" />} />
        <Route path="records">
          <Route index element={<Navigate replace to="pages" />} />
          <Route path="pages">
            <Route index element={<Navigate replace to="0" />} />
            <Route path=":pageNumber">
              <Route index element={<LogEntriesTab />} />
            </Route>
          </Route>
          {/* Must go before ":logEntryId", otherwise the router interprets "ai-submission" as a log entry id */}
          <Route
            path="ai-submission/:edpAiSubmissionId"
            element={
              <ErrorBoundary>
                <AiSubmissionPage />
              </ErrorBoundary>
            }
          />
          <Route
            path=":logEntryId"
            element={
              <ErrorBoundary>
                <SingleLogEntryPage />
              </ErrorBoundary>
            }
          />
        </Route>
        {/* this accounts for previous routing strategy, where views belonged to a single log */}
        <Route path="views/*" element={<RedirectToPageOnViewsTab />} />
        {isLogAiEnabled && (
          <Route path="documents" element={<LogDocumentsTab />} />
        )}
        <Route
          path="frequencySettings"
          element={<LogFrequencyRequirementsTab />}
        />
      </Route>
    </RoutesWith404>
  );
}
